import React, { useState } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Button from "../../../components/ui/Button"
import { Card, CardContent } from "../../../components/ui/card"
import { addScheduleEvent, updateScheduleEvent, removeScheduleEvent } from "../../../utils/cohortHelpers"

const EMPTY_FORM = { title: "", date: "", time: "", description: "" }

const CohortScheduleTab = ({ orgId, cohortId, events, onChange }) => {
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const openNew = () => {
    setForm(EMPTY_FORM)
    setEditingId(null)
    setShowForm(true)
    setError("")
  }

  const openEdit = (ev) => {
    setForm({ title: ev?.title || "", date: ev?.date || "", time: ev?.time || "", description: ev?.description || "" })
    setEditingId(ev?.id)
    setShowForm(true)
    setError("")
  }

  const handleSave = async () => {
    if (!form.title?.trim() || !form.date) {
      setError("Title and date are required")
      return
    }
    setSaving(true)
    const { error: saveError } = editingId
      ? await updateScheduleEvent(orgId, cohortId, editingId, form)
      : await addScheduleEvent(orgId, cohortId, form)
    setSaving(false)
    if (saveError) {
      setError(saveError?.message || "Could not save event")
      return
    }
    setShowForm(false)
    onChange?.()
  }

  const handleDelete = async (eventId) => {
    if (!window.confirm("Delete this schedule event?")) return
    await removeScheduleEvent(orgId, cohortId, eventId)
    onChange?.()
  }

  const sortedEvents = [...(events || [])].sort((a, b) => new Date(a?.date || 0) - new Date(b?.date || 0))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{events?.length || 0} event{events?.length === 1 ? "" : "s"}</p>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={openNew}>
          Add Event
        </Button>
      </div>

      {showForm && (
        <Card className="dark:bg-transparent">
          <CardContent className="pt-5 space-y-4">
            <Input label="Title" placeholder="e.g. Live Q&A Session" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e?.target?.value }))} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e?.target?.value }))} />
              <Input label="Time (optional)" type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e?.target?.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Description (optional)</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e?.target?.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setShowForm(false)}>Cancel</Button>
              <Button variant="default" fullWidth loading={saving} onClick={handleSave}>
                {editingId ? "Save Changes" : "Add Event"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {sortedEvents?.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Icon name="Calendar" size={24} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No schedule events yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedEvents?.map((ev) => (
            <div key={ev?.id} className="bg-card border border-border rounded-xl p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{ev?.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {ev?.date}{ev?.time ? ` - ${ev.time}` : ""}
                </p>
                {ev?.description && <p className="text-xs text-muted-foreground mt-1">{ev.description}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" iconName="Pencil" iconSize={14} onClick={() => openEdit(ev)} />
                <Button variant="ghost" size="icon" iconName="Trash2" iconSize={14} onClick={() => handleDelete(ev?.id)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CohortScheduleTab